import { useState, useRef, useEffect } from 'react'

export interface ZoomableImageProps {
  src: string
  alt: string
  isActive?: boolean
  className?: string
  style?: React.CSSProperties
  imgStyle?: React.CSSProperties
  onTransformChange?: (transform: { scale: number; position: { x: number; y: number } }) => void
  resetZoomOnSourceChange?: boolean
  overlayCanvasRef?: React.Ref<HTMLCanvasElement>
  showOverlay?: boolean
}

/**
 * ZoomableImage: Pure native-feel image zoom with zero visual clutter.
 * Supports touch pinch-to-zoom, pan while zoomed, double-tap zoom,
 * desktop wheel zoom, and desktop mouse-drag pan.
 */
export function ZoomableImage({
  src,
  alt,
  isActive = true,
  className,
  style,
  imgStyle,
  onTransformChange,
  resetZoomOnSourceChange = true,
  overlayCanvasRef,
  showOverlay = false,
}: ZoomableImageProps) {
  const [scale, setScale] = useState(1)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [decodedSrc, setDecodedSrc] = useState('')
  const [resetStateMarker, setResetStateMarker] = useState({ src, isActive, resetZoomOnSourceChange })
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)
  const imageReady = decodedSrc === src

  if (resetStateMarker.src !== src || resetStateMarker.isActive !== isActive || resetStateMarker.resetZoomOnSourceChange !== resetZoomOnSourceChange) {
    const sourceChanged = resetStateMarker.src !== src
    const becameInactive = resetStateMarker.isActive !== isActive && !isActive
    setResetStateMarker({ src, isActive, resetZoomOnSourceChange })
    if ((sourceChanged && resetZoomOnSourceChange) || becameInactive) {
      setScale(1)
      setPosition({ x: 0, y: 0 })
    }
  }

  useEffect(() => {
    onTransformChange?.({ scale, position })
  }, [onTransformChange, position, scale])

  const handleImageLoad = async (event: React.SyntheticEvent<HTMLImageElement>) => {
    const image = event.currentTarget
    const sourceAtLoad = image.currentSrc || image.src
    try {
      if (typeof image.decode === 'function') await image.decode()
    } catch {
      // Some WebViews reject decode() for a valid image; the load event still
      // confirms the complete resource is available for display.
    }
    if (image.naturalWidth > 0 && image.currentSrc === sourceAtLoad && sourceAtLoad === image.src) {
      setDecodedSrc(src)
    }
  }

  // The useful zoom limit depends on both the source image and the device
  // pixel density. At this scale, one source pixel maps to one screen pixel.
  const getMaxScale = () => {
    const image = imageRef.current
    if (!image || image.offsetWidth <= 0 || image.offsetHeight <= 0) return 7.5
    const devicePixelRatio = window.devicePixelRatio || 1
    const sourcePixelScale = Math.max(
      image.naturalWidth / (image.offsetWidth * devicePixelRatio),
      image.naturalHeight / (image.offsetHeight * devicePixelRatio),
    )
    return Math.max(sourcePixelScale * 2.2, 7.5)
  }

  const clampPosition = (next: { x: number; y: number }, nextScale: number) => {
    const container = containerRef.current
    const image = imageRef.current
    if (!container || !image) return next

    const scaledWidth = image.offsetWidth * nextScale
    const scaledHeight = image.offsetHeight * nextScale
    const maxPanX = Math.max(0, (scaledWidth - container.clientWidth) / 2)
    const maxPanY = Math.max(0, (scaledHeight - container.clientHeight) / 2)

    return {
      x: Math.min(Math.max(next.x, -maxPanX), maxPanX),
      y: Math.min(Math.max(next.y, -maxPanY), maxPanY),
    }
  }

  const touchStateRef = useRef({
    initialDist: 0,
    initialScale: 1,
    initialPos: { x: 0, y: 0 },
    initialFocalRel: { x: 0, y: 0 },
    lastTouch: { x: 0, y: 0 },
    lastTapTime: 0,
    isPinching: false,
    isPanning: false,
  })

  const isMouseDownRef = useRef(false)
  const mousePosRef = useRef({ x: 0, y: 0 })
  const scaleRef = useRef(scale)
  scaleRef.current = scale
  const positionRef = useRef(position)
  positionRef.current = position

  // Fully native touch and gesture handling to prevent any React synthetic event race conditions or jumps
  useEffect(() => {
    const el = containerRef.current
    if (!el) return

    const onTouchStartNative = (e: TouchEvent) => {
      if (!imageReady) return
      // Kill any running CSS transition immediately so gestures have 0ms latency
      if (imageRef.current) imageRef.current.style.transition = 'none'

      const currentScale = scaleRef.current
      const currentPos = positionRef.current

      if (e.touches.length === 2) {
        const t0 = e.touches[0]
        const t1 = e.touches[1]
        if (!t0 || !t1) return
        const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)
        const rect = el.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        const midX = (t0.clientX + t1.clientX) / 2
        const midY = (t0.clientY + t1.clientY) / 2

        touchStateRef.current.initialDist = dist
        touchStateRef.current.initialScale = currentScale
        touchStateRef.current.initialPos = { ...currentPos }
        touchStateRef.current.initialFocalRel = { x: midX - centerX, y: midY - centerY }
        touchStateRef.current.isPinching = true
        touchStateRef.current.isPanning = false
        setIsTransitioning(false)
      } else if (e.touches.length === 1) {
        const t0 = e.touches[0]
        if (!t0) return
        const now = Date.now()
        const timeSinceLast = now - touchStateRef.current.lastTapTime

        if (timeSinceLast < 280) {
          // Double-tap zoom toggle
          e.preventDefault()
          touchStateRef.current.lastTapTime = 0
          setIsTransitioning(true)
          if (currentScale > 1.2) {
            setScale(1)
            setPosition({ x: 0, y: 0 })
          } else {
            const rect = el.getBoundingClientRect()
            const tapX = t0.clientX - (rect.left + rect.width / 2)
            const tapY = t0.clientY - (rect.top + rect.height / 2)
            const nextScale = Math.min(2.6, getMaxScale())
            setPosition(
              clampPosition(
                { x: -tapX * (nextScale - 1), y: -tapY * (nextScale - 1) },
                nextScale,
              ),
            )
            setScale(nextScale)
          }
          return
        }

        touchStateRef.current.lastTapTime = now
        touchStateRef.current.lastTouch = { x: t0.clientX, y: t0.clientY }
        touchStateRef.current.initialPos = { ...currentPos }
        touchStateRef.current.isPanning = currentScale > 1.01
        touchStateRef.current.isPinching = false
        setIsTransitioning(false)
      }
    }

    const onTouchMoveNative = (e: TouchEvent) => {
      if (e.touches.length === 2 && touchStateRef.current.isPinching) {
        e.preventDefault()
        const t0 = e.touches[0]
        const t1 = e.touches[1]
        if (!t0 || !t1 || touchStateRef.current.initialDist <= 0) return
        const dist = Math.hypot(t0.clientX - t1.clientX, t0.clientY - t1.clientY)
        const rect = el.getBoundingClientRect()
        const centerX = rect.left + rect.width / 2
        const centerY = rect.top + rect.height / 2
        const midX = (t0.clientX + t1.clientX) / 2
        const midY = (t0.clientY + t1.clientY) / 2
        const currentFocalRel = { x: midX - centerX, y: midY - centerY }

        const factor = dist / touchStateRef.current.initialDist
        const targetScale = Math.min(
          Math.max(touchStateRef.current.initialScale * factor, 0.75),
          getMaxScale() * 1.35,
        )

        const initScale = touchStateRef.current.initialScale
        const initPos = touchStateRef.current.initialPos
        const initFocal = touchStateRef.current.initialFocalRel
        const contentX = (initFocal.x - initPos.x) / initScale
        const contentY = (initFocal.y - initPos.y) / initScale

        const targetX = currentFocalRel.x - contentX * targetScale
        const targetY = currentFocalRel.y - contentY * targetScale

        setScale(targetScale)
        // Keep focal point invariant under fingers without rigid pre-clamping
        setPosition({ x: targetX, y: targetY })
      } else if (e.touches.length === 1 && scaleRef.current > 1.01) {
        e.preventDefault()
        const t0 = e.touches[0]
        if (!t0) return
        const dx = t0.clientX - touchStateRef.current.lastTouch.x
        const dy = t0.clientY - touchStateRef.current.lastTouch.y
        touchStateRef.current.lastTouch = { x: t0.clientX, y: t0.clientY }

        setPosition((prev) => clampPosition({ x: prev.x + dx, y: prev.y + dy }, scaleRef.current))
      }
    }

    const onTouchEndNative = (e: TouchEvent) => {
      const currentScale = scaleRef.current
      if (touchStateRef.current.isPinching) {
        touchStateRef.current.isPinching = false
        if (currentScale < 1.02) {
          setIsTransitioning(true)
          setScale(1)
          setPosition({ x: 0, y: 0 })
        } else if (currentScale > getMaxScale()) {
          setIsTransitioning(true)
          const finalScale = getMaxScale()
          setScale(finalScale)
          setPosition((prev) => clampPosition(prev, finalScale))
        } else {
          setIsTransitioning(true)
          setPosition((prev) => clampPosition(prev, currentScale))
        }
      }

      if (e.touches.length === 1) {
        // Seamless handoff from 2 fingers to 1 finger
        const t0 = e.touches[0]
        if (t0) {
          touchStateRef.current.lastTouch = { x: t0.clientX, y: t0.clientY }
          touchStateRef.current.isPanning = scaleRef.current > 1.01
        }
      } else if (e.touches.length === 0) {
        touchStateRef.current.isPanning = false
        if (currentScale < 1.02) {
          setIsTransitioning(true)
          setScale(1)
          setPosition({ x: 0, y: 0 })
        } else {
          setIsTransitioning(true)
          setPosition((prev) => clampPosition(prev, currentScale))
        }
      }
    }

    const onWheelNative = (e: WheelEvent) => {
      if (!imageReady) return
      e.preventDefault()
      const delta = e.deltaY < 0 ? 0.25 : -0.25
      setIsTransitioning(true)
      setScale((prev) => {
        const next = Math.min(Math.max(Number((prev + delta).toFixed(2)), 1), getMaxScale())
        if (next === 1) setPosition({ x: 0, y: 0 })
        else setPosition((current) => clampPosition(current, next))
        return next
      })
    }

    el.addEventListener('touchstart', onTouchStartNative, { passive: false })
    el.addEventListener('touchmove', onTouchMoveNative, { passive: false })
    el.addEventListener('touchend', onTouchEndNative, { passive: false })
    el.addEventListener('touchcancel', onTouchEndNative, { passive: false })
    el.addEventListener('wheel', onWheelNative, { passive: false })

    return () => {
      el.removeEventListener('touchstart', onTouchStartNative)
      el.removeEventListener('touchmove', onTouchMoveNative)
      el.removeEventListener('touchend', onTouchEndNative)
      el.removeEventListener('touchcancel', onTouchEndNative)
      el.removeEventListener('wheel', onWheelNative)
    }
  }, [imageReady])

  const handleDoubleClick = () => {
    if (!imageReady) return
    setIsTransitioning(true)
    if (scale > 1.2) {
      setScale(1)
      setPosition({ x: 0, y: 0 })
    } else {
      setScale(Math.min(2.6, getMaxScale()))
      setPosition({ x: 0, y: 0 })
    }
  }

  const handleMouseDown = (e: React.MouseEvent) => {
    if (scale > 1.05) {
      e.preventDefault()
      isMouseDownRef.current = true
      mousePosRef.current = { x: e.clientX, y: e.clientY }
      setIsTransitioning(false)
    }
  }

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isMouseDownRef.current && scale > 1.05) {
      e.preventDefault()
      const dx = e.clientX - mousePosRef.current.x
      const dy = e.clientY - mousePosRef.current.y
      mousePosRef.current = { x: e.clientX, y: e.clientY }
      setPosition((prev) => clampPosition({ x: prev.x + dx, y: prev.y + dy }, scale))
    }
  }

  const handleMouseUp = () => {
    isMouseDownRef.current = false
  }

  return (
    <div
      ref={containerRef}
      className={`studio-zoomable-container ${className || ''}`}
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden',
        touchAction: scale > 1 ? 'none' : 'pan-x pan-y',
        position: 'relative',
        userSelect: 'none',
        WebkitUserSelect: 'none',
        ...style,
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onDoubleClick={handleDoubleClick}
    >
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        draggable={false}
        loading="eager"
        decoding="async"
        fetchPriority="high"
        onLoad={handleImageLoad}
        onError={() => setDecodedSrc('')}
        style={{
          transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
          transition: [
            isTransitioning ? 'transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
            imageReady && !overlayCanvasRef ? 'opacity 120ms ease-out' : 'none',
          ].join(', '),
          transformOrigin: 'center center',
          maxWidth: '100%',
          maxHeight: '100%',
          objectFit: 'contain',
          display: 'block',
          opacity: imageReady && !showOverlay ? 1 : 0,
          borderRadius: '18px',
          boxShadow: 'none',
          cursor: scale > 1 ? 'grab' : 'zoom-in',
          ...imgStyle,
        }}
      />
      {overlayCanvasRef && (
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: style?.alignItems || 'center',
            justifyContent: style?.justifyContent || 'center',
            overflow: 'hidden',
            pointerEvents: 'none',
            visibility: showOverlay ? 'visible' : 'hidden',
          }}
        >
          <canvas
            ref={overlayCanvasRef}
            style={{
              display: 'block',
              maxWidth: '100%',
              maxHeight: '100%',
              width: 'auto',
              height: 'auto',
              objectFit: 'contain',
              borderRadius: '18px',
              transform: `translate3d(${position.x}px, ${position.y}px, 0) scale(${scale})`,
              transition: isTransitioning ? 'transform 0.22s cubic-bezier(0.16, 1, 0.3, 1)' : 'none',
              transformOrigin: 'center center',
            }}
          />
        </div>
      )}
      {!imageReady && (
        <div
          role="status"
          aria-label={alt}
          style={{
            position: 'absolute',
            width: 24,
            height: 24,
            border: '2px solid rgba(255,255,255,.22)',
            borderTopColor: 'rgba(255,255,255,.86)',
            borderRadius: '50%',
            pointerEvents: 'none',
          }}
        />
      )}
    </div>
  )
}
